import { zeroErrorMessage } from "@/lib/zero-error";
import {
  useConnectionState,
  useQuery as useZeroQuery,
  useZero,
} from "@rocicorp/zero/react";
import { useMemo, useState } from "react";
import { appMutators, appQueries } from "@production-hub/shared";
import type { ZeroProjectRow } from "@production-hub/shared";
import { Tables, TablesInsert, TablesUpdate } from "@/integrations/neon/types";
import { toast } from "sonner";
import { generateUUID } from "@/lib/utils";
import {
  getUpcomingProjectSortDate,
  isUpcomingProject,
  UPCOMING_PROJECT_LIMIT,
} from "@/lib/upcoming-projects";

export type Project = Tables<"projects">;
export type ProjectInsert = TablesInsert<"projects">;
export type ProjectUpdate = TablesUpdate<"projects">;

export type ProjectWithRelations = Project & {
  companies: {
    name: string;
    invoice_address: string | null;
    number: string | null;
    language: string | null;
  } | null;
  contact: { first_name: string; last_name: string } | null;
  project_manager: { first_name: string; last_name: string } | null;
  account_manager: { first_name: string; last_name: string } | null;
  technical_responsible: { first_name: string; last_name: string } | null;
  equipment_responsible: { first_name: string; last_name: string } | null;
  project_groups: { name: string } | null;
};
export type ZeroProjectWithRelations = ZeroProjectRow & {
  companies?: ZeroCompanyRow | null;
  contact?: ZeroPersonRow | null;
  project_manager?: ZeroPersonRow | null;
  account_manager?: ZeroPersonRow | null;
  technical_responsible?: ZeroPersonRow | null;
  equipment_responsible?: ZeroPersonRow | null;
  project_groups?: ZeroProjectGroupRow | null;
};
type ZeroDateInput = string | number | null;
export type ZeroProjectUpdate = {
  -readonly [K in
    | "number"
    | "name"
    | "customerID"
    | "contactID"
    | "projectGroupID"
    | "studioID"
    | "accountManagerID"
    | "projectManagerID"
    | "technicalResponsibleID"
    | "equipmentResponsibleID"
    | "status"
    | "types"
    | "notes"
    | "conditions"
    | "language"
    | "defaultVatRate"
    | "defaultDiscountPercent"
    | "crewListRemarks"
    | "mealsRemarks"
    | "hotelsRemarks"
    | "productionLocation"
    | "productionMode"
    | "rentmanID"]?: ZeroProjectRow[K];
} & {
  startDate?: ZeroDateInput;
  endDate?: ZeroDateInput;
  eventStartDate?: ZeroDateInput;
  eventEndDate?: ZeroDateInput;
  planningStartDate?: ZeroDateInput;
  planningEndDate?: ZeroDateInput;
};

export type UpcomingProject = {
  availability_status: string;
  project: ProjectWithRelations;
};

type MutationOptions<T> = { onSuccess?: (data: T) => void };
type ServerMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ServerMutationResult>;
  };
};
type ZeroPersonRow = {
  firstName: string;
  lastName: string;
};
type ZeroCompanyRow = {
  name: string;
  invoiceAddress?: string | null;
  number?: string | null;
  language?: string | null;
};
type ZeroProjectGroupRow = {
  name: string;
};
type ZeroUpcomingProjectRow = {
  status: string;
  project?: ZeroProjectWithRelations | null;
};

function assertServerMutationSucceeded(result: ServerMutationResult) {
  if (result.type === "error") {
    throw new Error(result.error.message);
  }
}

function reportServerMutationFailure(
  server: Promise<ServerMutationResult>,
  message: string,
) {
  void server.then(assertServerMutationSucceeded).catch((error: Error) => {
    toast.error(`${message}: ${error.message}`);
  });
}

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? null;
}

function toLegacyDate(value: number | string | null | undefined) {
  if (typeof value === "number")
    return new Date(value).toISOString().slice(0, 10);
  return value ?? null;
}

function toLegacyPerson(row: ZeroPersonRow | null | undefined) {
  return row
    ? {
        first_name: row.firstName,
        last_name: row.lastName,
      }
    : null;
}

function toLegacyProject(row: ZeroProjectWithRelations): ProjectWithRelations {
  return {
    id: row.id,
    name: row.name,
    number: row.number ?? null,
    customer_id: row.customerID ?? null,
    contact_id: row.contactID ?? null,
    project_group_id: row.projectGroupID ?? null,
    studio_id: row.studioID ?? null,
    start_date: toLegacyDate(row.startDate),
    end_date: toLegacyDate(row.endDate),
    notes: row.notes ?? null,
    account_manager_id: row.accountManagerID ?? null,
    project_manager_id: row.projectManagerID ?? null,
    technical_responsible_id: row.technicalResponsibleID ?? null,
    equipment_responsible_id: row.equipmentResponsibleID ?? null,
    status: row.status ?? null,
    types: row.types ?? [],
    created_at: toLegacyTimestamp(row.createdAt) ?? "",
    updated_at: toLegacyTimestamp(row.updatedAt) ?? "",
    deleted_at: toLegacyTimestamp(row.deletedAt),
    version: row.version ?? null,
    default_vat_rate: row.defaultVatRate ?? "CH_8_1",
    default_discount_percent: row.defaultDiscountPercent ?? 0,
    event_start_date: toLegacyDate(row.eventStartDate),
    event_end_date: toLegacyDate(row.eventEndDate),
    planning_start_date: toLegacyDate(row.planningStartDate),
    planning_end_date: toLegacyDate(row.planningEndDate),
    rentman_id: row.rentmanID ?? null,
    conditions: row.conditions ?? null,
    clockify_project_id: row.clockifyProjectID ?? null,
    clockify_synced_at: toLegacyTimestamp(row.clockifySyncedAt),
    language: row.language ?? "de",
    crew_list_remarks: row.crewListRemarks ?? null,
    meals_remarks: row.mealsRemarks ?? null,
    hotels_remarks: row.hotelsRemarks ?? null,
    production_location: row.productionLocation ?? null,
    production_mode: row.productionMode ?? null,
    companies: row.companies
      ? {
          name: row.companies.name,
          invoice_address: row.companies.invoiceAddress ?? null,
          number: row.companies.number ?? null,
          language: row.companies.language ?? null,
        }
      : null,
    contact: toLegacyPerson(row.contact),
    project_manager: toLegacyPerson(row.project_manager),
    account_manager: toLegacyPerson(row.account_manager),
    technical_responsible: toLegacyPerson(row.technical_responsible),
    equipment_responsible: toLegacyPerson(row.equipment_responsible),
    project_groups: row.project_groups
      ? {
          name: row.project_groups.name,
        }
      : null,
  };
}

function toZeroInputDate(value: number | string | null | undefined) {
  if (typeof value === "number")
    return new Date(value).toISOString().slice(0, 10);
  return value ?? null;
}

function normalizeZeroProjectUpdate(updates: ZeroProjectUpdate) {
  const input: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(updates)) {
    if (
      key === "startDate" ||
      key === "endDate" ||
      key === "eventStartDate" ||
      key === "eventEndDate" ||
      key === "planningStartDate" ||
      key === "planningEndDate"
    ) {
      input[key] = toZeroInputDate(value as number | string | null | undefined);
    } else {
      input[key] = value ?? null;
    }
  }

  return input;
}

function toZeroProjectInput(project: ProjectInsert, id = generateUUID()) {
  return {
    id,
    number: project.number ?? null,
    name: project.name,
    customerID: project.customer_id ?? null,
    contactID: project.contact_id ?? null,
    projectGroupID: project.project_group_id ?? null,
    studioID: project.studio_id ?? null,
    accountManagerID: project.account_manager_id ?? null,
    projectManagerID: project.project_manager_id ?? null,
    technicalResponsibleID: project.technical_responsible_id ?? null,
    equipmentResponsibleID: project.equipment_responsible_id ?? null,
    status: project.status ?? "initial_contact",
    types: project.types ?? [],
    startDate: project.start_date ?? null,
    endDate: project.end_date ?? null,
    eventStartDate: project.event_start_date ?? null,
    eventEndDate: project.event_end_date ?? null,
    planningStartDate: project.planning_start_date ?? null,
    planningEndDate: project.planning_end_date ?? null,
    notes: project.notes ?? null,
    conditions: project.conditions ?? null,
    language: (project.language ?? "de") as ZeroProjectRow["language"],
    defaultVatRate: project.default_vat_rate ?? "CH_8_1",
    defaultDiscountPercent: project.default_discount_percent ?? 0,
    crewListRemarks: project.crew_list_remarks ?? null,
    mealsRemarks: project.meals_remarks ?? null,
    hotelsRemarks: project.hotels_remarks ?? null,
    productionLocation: project.production_location ?? null,
    productionMode: project.production_mode ?? null,
    rentmanID: project.rentman_id ?? null,
    clockifyProjectID: null,
    clockifySyncedAt: null,
    deletedAt: null,
    version: 1,
  };
}

function toZeroRowDate(value: string | null) {
  return value ? Date.parse(value) : null;
}

function toZeroProjectUpdates(updates: ProjectUpdate) {
  const input: Record<string, unknown> = {};
  const map: Record<string, string> = {
    number: "number",
    name: "name",
    customer_id: "customerID",
    contact_id: "contactID",
    project_group_id: "projectGroupID",
    studio_id: "studioID",
    account_manager_id: "accountManagerID",
    project_manager_id: "projectManagerID",
    technical_responsible_id: "technicalResponsibleID",
    equipment_responsible_id: "equipmentResponsibleID",
    status: "status",
    types: "types",
    start_date: "startDate",
    end_date: "endDate",
    event_start_date: "eventStartDate",
    event_end_date: "eventEndDate",
    planning_start_date: "planningStartDate",
    planning_end_date: "planningEndDate",
    notes: "notes",
    conditions: "conditions",
    language: "language",
    default_vat_rate: "defaultVatRate",
    default_discount_percent: "defaultDiscountPercent",
    crew_list_remarks: "crewListRemarks",
    meals_remarks: "mealsRemarks",
    hotels_remarks: "hotelsRemarks",
    production_location: "productionLocation",
    production_mode: "productionMode",
    rentman_id: "rentmanID",
  };

  for (const [legacyKey, zeroKey] of Object.entries(map)) {
    if (legacyKey in updates) {
      input[zeroKey] = updates[legacyKey as keyof ProjectUpdate] ?? null;
    }
  }

  return input;
}

export function useProjectWriteAvailability() {
  const state = useConnectionState();
  return {
    canWrite: state.name === "connected",
    reason:
      state.name === "connected"
        ? null
        : state.name === "needs-auth"
          ? "Needs auth"
          : "Offline, read-only",
  };
}

export function useMyUpcomingProjects(personId: string | null) {
  const query = personId
    ? appQueries.projects.myUpcoming({ personID: personId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!personId });
  const today = Date.UTC(
    new Date().getUTCFullYear(),
    new Date().getUTCMonth(),
    new Date().getUTCDate(),
  );

  const rows = useMemo(() => {
    const upcomingRows = ((data ?? []) as ZeroUpcomingProjectRow[])
      .filter((row) => row.project)
      .map((row) => ({
        availability_status: row.status,
        project: toLegacyProject(row.project),
      }))
      .filter((row) =>
        isUpcomingProject(row.project, today),
      ) as UpcomingProject[];

    upcomingRows.sort((a, b) => {
      const aDate = getUpcomingProjectSortDate(a.project) ?? "";
      const bDate = getUpcomingProjectSortDate(b.project) ?? "";
      return aDate.localeCompare(bDate);
    });

    return upcomingRows;
  }, [data, today]);

  return {
    data: rows.slice(0, UPCOMING_PROJECT_LIMIT),
    isLoading: details.type !== "complete" && rows.length === 0,
  };
}

export function useProjects(includeDeleted = false) {
  const [data, details] = useZeroQuery(appQueries.projects.list());

  const projects = useMemo(
    () =>
      ((data ?? []) as ZeroProjectWithRelations[])
        .filter((project) => includeDeleted || project.deletedAt == null)
        .map(toLegacyProject),
    [data, includeDeleted],
  );
  return {
    data: projects,
    isLoading: details.type !== "complete" && projects.length === 0,
  };
}

export function useProject(id: string | undefined) {
  const [data, details] = useZeroQuery(appQueries.projects.list());
  const project =
    id && data
      ? ((data as ZeroProjectWithRelations[]).find((row) => row.id === id) ??
        null)
      : null;

  return {
    data: project ? toLegacyProject(project) : null,
    isLoading: !!id && details.type !== "complete" && !project,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useZeroProject(id: string | undefined) {
  const [data, details] = useZeroQuery(appQueries.projects.list());
  const project =
    id && data
      ? (((data as ZeroProjectWithRelations[]).find((row) => row.id === id) as
          | ZeroProjectWithRelations
          | undefined) ?? null)
      : null;

  return {
    data: project,
    isLoading: !!id && details.type !== "complete" && !project,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateProject() {
  const zero = useZero() as ZeroMutateClient;
  const { canWrite } = useProjectWriteAvailability();
  const [isPending, setIsPending] = useState(false);

  const run = async (project: ProjectInsert) => {
    if (!canWrite) throw new Error("Offline, read-only");
    const id = generateUUID();
    const input = toZeroProjectInput(project, id);
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.projects.create(input));
      await mutation.client;
      reportServerMutationFailure(
        mutation.server,
        "Project create was rejected",
      );
      return toLegacyProject({
        ...input,
        startDate: toZeroRowDate(input.startDate),
        endDate: toZeroRowDate(input.endDate),
        eventStartDate: toZeroRowDate(input.eventStartDate),
        eventEndDate: toZeroRowDate(input.eventEndDate),
        planningStartDate: toZeroRowDate(input.planningStartDate),
        planningEndDate: toZeroRowDate(input.planningEndDate),
        language: input.language as ZeroProjectWithRelations["language"],
        defaultVatRate:
          input.defaultVatRate as ZeroProjectWithRelations["defaultVatRate"],
        productionMode:
          input.productionMode as ZeroProjectWithRelations["productionMode"],
        clockifyProjectID: null,
        clockifySyncedAt: null,
        deletedAt: null,
        version: 1,
        createdAt: 0,
        updatedAt: 0,
      } as ZeroProjectWithRelations);
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (project: ProjectInsert, options?: MutationOptions<Project>) => {
      void run(project)
        .then((result) => {
          toast.success("Project created successfully");
          options?.onSuccess?.(result);
        })
        .catch((error: Error) => {
          toast.error("Failed to create project: " + error.message);
        });
    },
    mutateAsync: run,
    isPending,
    isDisabled: !canWrite,
  };
}

export function useUpdateProject() {
  const zero = useZero() as ZeroMutateClient;
  const { canWrite } = useProjectWriteAvailability();
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    id,
    updates,
  }: {
    id: string;
    updates: ProjectUpdate;
  }) => {
    if (!canWrite) throw new Error("Offline, read-only");
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projects.update({
          id,
          ...toZeroProjectUpdates(updates),
        }),
      );
      await mutation.client;
      reportServerMutationFailure(
        mutation.server,
        "Project update was rejected",
      );
      return { id } as Project;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      { id, updates }: { id: string; updates: ProjectUpdate },
      options?: MutationOptions<Project>,
    ) => {
      void run({ id, updates })
        .then((result) => {
          toast.success("Project updated successfully");
          options?.onSuccess?.(result);
        })
        .catch((error: Error) => {
          toast.error("Failed to update project: " + error.message);
        });
    },
    mutateAsync: run,
    isPending,
    isDisabled: !canWrite,
  };
}

export function useUpdateZeroProject() {
  const zero = useZero() as ZeroMutateClient;
  const { canWrite } = useProjectWriteAvailability();
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    id,
    updates,
  }: {
    id: string;
    updates: ZeroProjectUpdate;
  }) => {
    if (!canWrite) throw new Error("Offline, read-only");
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.projects.update({
          id,
          ...normalizeZeroProjectUpdate(updates),
        }),
      );
      await mutation.client;
      reportServerMutationFailure(
        mutation.server,
        "Project update was rejected",
      );
      return { id } as ZeroProjectRow;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      { id, updates }: { id: string; updates: ZeroProjectUpdate },
      options?: MutationOptions<ZeroProjectRow>,
    ) => {
      void run({ id, updates })
        .then((result) => {
          options?.onSuccess?.(result);
        })
        .catch((error: Error) => {
          toast.error("Failed to update project: " + error.message);
        });
    },
    mutateAsync: run,
    isPending,
    isDisabled: !canWrite,
  };
}

export function useSoftDeleteProject() {
  const zero = useZero() as ZeroMutateClient;
  const { canWrite } = useProjectWriteAvailability();
  const [isPending, setIsPending] = useState(false);

  const run = async (id: string) => {
    if (!canWrite) throw new Error("Offline, read-only");
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.projects.softDelete({ id }));
      await mutation.client;
      reportServerMutationFailure(
        mutation.server,
        "Project delete was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (id: string, options?: MutationOptions<void>) => {
      void run(id)
        .then(() => {
          toast.success("Project moved to trash");
          options?.onSuccess?.();
        })
        .catch((error: Error) => {
          toast.error("Failed to delete project: " + error.message);
        });
    },
    mutateAsync: run,
    isPending,
    isDisabled: !canWrite,
  };
}

export function useRestoreProject() {
  const zero = useZero() as ZeroMutateClient;
  const { canWrite } = useProjectWriteAvailability();
  const [isPending, setIsPending] = useState(false);

  const run = async (id: string) => {
    if (!canWrite) throw new Error("Offline, read-only");
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.projects.restore({ id }));
      await mutation.client;
      reportServerMutationFailure(
        mutation.server,
        "Project restore was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (id: string, options?: MutationOptions<void>) => {
      void run(id)
        .then(() => {
          toast.success("Project restored");
          options?.onSuccess?.();
        })
        .catch((error: Error) => {
          toast.error("Failed to restore project: " + error.message);
        });
    },
    mutateAsync: run,
    isPending,
    isDisabled: !canWrite,
  };
}
